/**
 * PHARMAKON — UI configuration (Novera Pro foundations, refonte D-23)
 *
 * Single home of the display constants the shell and the primitives read
 * (design/ux/01_foundations.md §2.9, §7.2 "Config, not literals"). Later
 * foundation steps add their sections here (hover delays, breakpoints, grid
 * column-priority thresholds) — never as literals inside a component.
 *
 * Owner class: build-time (rules/development.md §4.2). User preferences
 * (theme, density) stay in localStorage under THEME_STORAGE_KEY (OP-F-06).
 */

// ── Themes (§2.9) ───────────────────────────────────────────────────────────
/** Resolved themes written to <html data-theme>. */
export const THEMES = Object.freeze({
  CLAIR: "novera",
  SOMBRE: "novera-dark",
});

/** User preference: a resolved theme or "auto" (follows the OS). */
export const THEME_AUTO = "auto";

export const DEFAULT_THEME = THEMES.CLAIR;
export const DEFAULT_THEME_PREF = THEMES.CLAIR;

/**
 * The three choices offered in the UI (display-preferences panel, login page,
 * portal topbar). The legacy green light and neon dark themes stay in CSS for
 * one release but are no longer offered (OP-F-02).
 */
export const THEME_OPTIONS = Object.freeze([
  Object.freeze({ value: THEMES.CLAIR, label: "Clair", icon: "sun" }),
  Object.freeze({ value: THEMES.SOMBRE, label: "Sombre", icon: "moon" }),
  Object.freeze({ value: THEME_AUTO, label: "Automatique", icon: "monitor" }),
]);

/**
 * Persisted values written before the Novera Pro consolidation → current
 * preference. Anything not listed (and not current) falls back to the default.
 */
export const LEGACY_THEME_PREF_MIGRATION = Object.freeze({
  light: THEMES.CLAIR,
  dark: THEMES.SOMBRE,
  novera: THEMES.CLAIR,
  "novera-dark": THEMES.SOMBRE,
  auto: THEME_AUTO,
});

/** OS dark-mode query used to resolve the "auto" preference. */
export const PREFERS_DARK_QUERY = "(prefers-color-scheme: dark)";

// ── Density (§2.5, OP-F-05) ─────────────────────────────────────────────────
export const DENSITIES = Object.freeze({
  COMFORTABLE: "comfortable",
  COMPACT: "compact",
});
export const DEFAULT_DENSITY = DENSITIES.COMFORTABLE;
export const DENSITY_OPTIONS = Object.freeze([
  Object.freeze({ value: DENSITIES.COMFORTABLE, label: "Confortable" }),
  Object.freeze({ value: DENSITIES.COMPACT, label: "Compacte" }),
]);

// ── Persistence ─────────────────────────────────────────────────────────────
export const THEME_STORAGE_KEY = "pharmakon_theme";
/** Version of the persisted display preferences (1 = Novera Pro, WP-14). */
export const THEME_STORAGE_VERSION = 1;

// ── Shells (§5.1, A-20) ─────────────────────────────────────────────────────
/** Value of `data-shell` on `.app-grid`; the pharmacy rules are scoped to it. */
export const SHELLS = Object.freeze({
  PHARMACY: "pharmacy",
  FOURNISSEUR: "fournisseur",
});

// ─── WP-15 · F1a primitives (ux/01 §3.7-§3.12) ───────────────────────────────

/**
 * Toast lifetimes by tone (ux/01 §3.9): success 4 s and info 5 s, warning 8 s,
 * error persistent until dismissed. `0` = persistent. A caller may ask for a
 * LONGER lifetime, never a shorter one (a 55+ reader must have time to read).
 */
export const TOAST_LIFETIME_MS = Object.freeze({
  success: 4000,
  info: 5000,
  warning: 8000,
  error: 0,
  loading: 0,
});
/** At most this many toasts are stacked on screen (the newest ones). */
export const TOAST_MAX_VISIBLE = 3;
/** Tones announced assertively (role="alert"); the others use role="status". */
export const TOAST_ALERT_TONES = Object.freeze(["warning", "error"]);

/** « Chargement… » appears only after this delay (ux/01 §3.12). */
export const LOADING_TEXT_DELAY_MS = 400;

/** Dialogs never stack deeper than this (ux/01 §3.7). */
export const MAX_DIALOG_DEPTH = 2;

/**
 * A row that a list page receives focus on after a create elsewhere (XD-27: the new fournisseur on
 * `/fournisseurs`) keeps the selected fill for this long, then reads like any other row.
 */
export const ROW_ARRIVAL_HIGHLIGHT_MS = 1500;

// ─── WP-16 · F1b grid, combobox, panels, tabs, header (ux/01 §3.4-§3.6, §3.13-§3.14, §5) ──

/** Layout breakpoints of ux/01 §5.1 (viewport CSS px, lower bounds). */
export const BREAKPOINTS = Object.freeze({
  TABLET: 768,
  DESKTOP_SMALL: 1024,
  DESKTOP: 1280,
  WIDE: 1600,
});

/**
 * Column priorities resolved on the GRID CONTAINER width (ux/01 §5.3):
 * priority 1 always, 2 from 1000 px, 3 from 1200 px.
 */
export const GRID_PRIORITY_MIN_WIDTH = Object.freeze({ 1: 0, 2: 1000, 3: 1200 });

/** Sort directions sent to the server (`sort_order`) and used client-side. */
export const SORT_ORDERS = Object.freeze({ ASC: "asc", DESC: "desc" });
/** Query-string / API parameter names of a server-sorted list (cross-module §3.2). */
export const SORT_PARAMS = Object.freeze({ BY: "sort_by", ORDER: "sort_order" });

/**
 * Largest dataset a grid may sort in the browser (cross-module 02_DESIGN §3.1
 * `LISTE.tri_client_max`): above it the list must be server-sorted.
 */
export const GRID_CLIENT_SORT_MAX = 200;
/** Rows per page of a server-paged grid (ux/01 §3.5 « 50 or 100 »). */
export const GRID_PAGE_SIZES = Object.freeze([50, 100]);
export const GRID_DEFAULT_PAGE_SIZE = 50;
/** PgUp / PgDn step when the body height is unknown (rows). */
export const GRID_PAGE_STEP_FALLBACK = 10;
/** Default body height of a grid that owns its scroll container. */
export const GRID_DEFAULT_MAX_HEIGHT = "70vh";
/** localStorage keys: per-grid « Colonnes » choices and tree expansion. */
export const GRID_COLUMNS_STORAGE_PREFIX = "pharmakon_grid_cols:";
export const GRID_TREE_STORAGE_PREFIX = "pharmakon_grid_tree:";
/** Inline row actions before « Plus d'actions » (ux/01 §3.5). */
export const GRID_MAX_INLINE_ROW_ACTIONS = 2;
/**
 * Context menu (refonte WP-100, lib/menu.js): the gap kept between the menu and the viewport edges,
 * and how far right of the row's first data cell a menu opened from the keyboard starts (px).
 */
export const CONTEXT_MENU_VIEWPORT_MARGIN = 8;
export const CONTEXT_MENU_ROW_INSET = 16;

/** Combobox (ux/01 §3.4, A-11): debounce, minimum query, visible rows. */
export const COMBOBOX_DEBOUNCE_MS = 200;
/** A list page's free-text search (catalogue, stock): one server read once the typing pauses (XWP-12). */
export const LISTE_RECHERCHE_DEBOUNCE_MS = 300;

/**
 * Cross-module X XWP-13 (refonte ux/04 §1.1.3) — rows per page of « Liste des produits » (today 20
 * in the legacy store): 50, server-paged; the server caps `limit` itself.
 */
export const PRODUITS_PAGE_TAILLE = 50;
export const COMBOBOX_MIN_CHARS = 2;
export const COMBOBOX_MAX_VISIBLE = 8;

/** SplitPane (ux/01 §3.5 master-detail): keyboard step and bounds. */
export const SPLITPANE_STEP_PX = 40;
export const SPLITPANE_DEFAULT_RATIO = 0.4;
export const SPLITPANE_MIN_PX = 120;

/**
 * PageHeader actions shown inline before « Plus… » (ux/01 §3.13, §5.4):
 * 3 on desktop, 2 on a tablet, the primary only on a phone.
 */
export const PAGE_HEADER_INLINE_ACTIONS = Object.freeze({
  DESKTOP: 3,
  TABLET: 2,
  PHONE: 1,
});

// ─── WP-17 · F2 keyboard layer (ux/01 §4, §6.5) ──────────────────────────────

/**
 * Shown instead of navigating when a screen holding unsaved work suppresses
 * the global navigation keys (ux/01 §4.3 rule 6 — the POS while a sale is in
 * progress). A screen may pass its own sentence.
 */
export const NAV_SUPPRESSED_MESSAGE = "Terminez ou mettez en attente la vente en cours";

/**
 * Overlays that suspend the screen map (§4.3 rule 5) besides the Modal /
 * Drawer / ConfirmDialog stack (lib/dialog.js): any open `aria-modal` dialog
 * (the POS F1 sheet, the alertes-équipe console, legacy modals).
 */
export const MODAL_OVERLAY_SELECTOR = '[aria-modal="true"]';

/** Command palette (§6.5): « Récents » size and storage, product hits shown. */
export const PALETTE_RECENTS_MAX = 8;
export const PALETTE_RECENTS_STORAGE_PREFIX = "pharmakon_palette_recents:";
export const PALETTE_PRODUCT_LIMIT = 5;
/** Pages listed for a typed query (best first) so the « Produits » group stays in view. */
export const PALETTE_PAGE_LIMIT = 8;
/** Command verbs a typed query lists before the pages (Trésorerie UX 01 §2.6: six at most per role). */
export const PALETTE_VERB_LIMIT = 6;
/**
 * « Lots » group (refonte WP-37 / XWP-12, OP-PSA-33): lots whose N° lot or lot label is exactly the
 * query, at most PALETTE_LOT_LIMIT shown, read from one page of PALETTE_LOT_FETCH lot rows of
 * `GET /stock/lots?search=` (the server matches the exact N° lot / lot barcode there).
 */
export const PALETTE_LOT_LIMIT = 3;
export const PALETTE_LOT_FETCH = 20;

// ─── WP-18 · F3 shell and navigation (ux/01 §6) ──────────────────────────────

/** Flyout cascade timings (§6.4 « Mouse », D-21). */
export const NAV_HOVER_OPEN_MS = 150;
export const NAV_HOVER_CLOSE_MS = 300;
/** Type-ahead buffer lifetime in the rail, the panels and the drawer (§6.4). */
export const NAV_TYPEAHEAD_RESET_MS = 500;
/** A flyout panel lists at most this many items without scrolling (§6.4). */
export const NAV_PANEL_MAX_ITEMS = 10;
/**
 * Rail mode by viewport (§5.1, §6.4 « Compact rail »): expanded from 1280 px
 * (unless the user collapsed it), compact from 1024 to 1279 px whatever the
 * persisted choice, the drawer below 1024 px.
 */
export const RAIL_MODES = Object.freeze({
  EXPANDED: "expanded",
  COMPACT: "compact",
  DRAWER: "drawer",
});
/** Product name of the window title (§6.3). */
export const APP_TITLE = "Pharmakon";
/** Window title when the location is not a menu page (index.html). */
export const APP_DEFAULT_TITLE = "Pharmakon — Système de gestion pharmaceutique";
/** Query parameter a deep link uses to open a topbar panel (§6.14). */
export const SHELL_PANEL_PARAM = "panel";
export const SHELL_PANELS = Object.freeze({ NOTIFICATIONS: "notifications" });
/**
 * The topbar's stock-alert entries re-read their counts when the window
 * regains focus and the last read is older than this (never on a timer).
 */
export const STOCK_ALERTS_STALE_MS = 5 * 60 * 1000;

/**
 * Cross-module X XWP-15 (XA-17, XMD-01-19) — the browser side of the server's
 * `INVALIDATION_STOCK` (`apps/server/src/config/stock-liste.config.js`): stock
 * writes publish the keys they touched on topic `stock` (coarse « stock:lots »,
 * « stock:alertes », « stock:facettes », fine « lot:<id> », « produit:<id> »,
 * « magasin:<id> »); a mounted Stock & lots view re-reads only when a key it
 * shows is named. Same value as the server (parity: lib/__tests__/stock-lots.test.js).
 */
export const INVALIDATION_STOCK_UI = Object.freeze({ sujet: "stock" });

/**
 * X XWP-16 — the key grammar of topic `stock` the magasins workspace reads (server `CLES_STOCK`,
 * parity: lib/__tests__/magasins.test.js): coarse keys and the prefixes of the fine ones.
 */
export const CLES_STOCK_UI = Object.freeze({
  lots: "stock:lots",
  alertes: "stock:alertes",
  facettes: "stock:facettes",
  magasins: "magasins:vue-ensemble",
  prefixeLot: "lot:",
  prefixeProduit: "produit:",
  prefixeMagasin: "magasin:",
  prefixeEmplacement: "emplacement:",
});

/** X XWP-15 — rows per page of Stock & lots, the corrections journal and the risk ranking. */
export const STOCK_PAGE_TAILLE = 50;
